/**
 * Element metadata extraction utilities
 */

export interface ElementMetadata {
  componentName: string | null;
  filePath?: string;
  domPath: string;
  boundingBox: DOMRect;
  innerHTML: string;
}

/**
 * Extract metadata from a DOM element
 */
export function extractElementMetadata(element: Element): ElementMetadata {
  return {
    componentName: getReactComponentName(element),
    domPath: getDomPath(element),
    boundingBox: element.getBoundingClientRect(),
    innerHTML: element.innerHTML.slice(0, 200), // First 200 chars for matching
  };
}

/**
 * Get React component name from element using Fiber
 */
function getReactComponentName(element: Element): string | null {
  // Find React Fiber internal property
  const fiberKey = Object.keys(element).find(
    (key) =>
      key.startsWith('__reactFiber') ||
      key.startsWith('__reactInternalInstance')
  );

  if (!fiberKey) {
    console.log('No React Fiber found on element');
    return null;
  }

  let fiber = (element as any)[fiberKey];

  // Walk up the fiber tree to find a component
  while (fiber) {
    // Function component or class component
    if (fiber.type && typeof fiber.type === 'function') {
      const name = fiber.type.name || fiber.type.displayName;
      if (name && name !== 'Unknown') {
        console.log('Found component:', name);
        return name;
      }
    }

    // Intrinsic element (div, span, etc.) - skip
    if (fiber.type && typeof fiber.type === 'string') {
      fiber = fiber.return;
      continue;
    }

    fiber = fiber.return;
  }

  console.log('No component name found in Fiber tree');
  return null;
}

/**
 * Generate CSS selector path for element
 */
function getDomPath(element: Element): string {
  const path: string[] = [];
  let current: Element | null = element;

  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();

    // Add ID if present
    if (current.id) {
      selector += `#${current.id}`;
    }

    // Add significant classes (filter out utility/generated classes)
    const classes = Array.from(current.classList)
      .filter((c) => {
        // Filter out common utility/generated class patterns
        return (
          !c.startsWith('css-') && // Emotion/styled-components
          !c.startsWith('_') && // CSS modules hash
          !c.match(/^[a-f0-9]{6,}$/) // Hash-only classes
        );
      })
      .join('.');

    if (classes) {
      selector += `.${classes}`;
    }

    path.unshift(selector);
    current = current.parentElement;
  }

  return path.join(' > ');
}

/**
 * Check if element is part of the DevFix system
 * (to avoid selecting overlay, toolbar, etc.)
 */
export function isSystemElement(element: Element | null): boolean {
  if (!element) return true;

  // Check if element or any parent has devfix-related classes/IDs
  let current: Element | null = element;
  while (current) {
    const className = current.className;
    if (
      typeof className === 'string' &&
      (className.includes('devfix') || className.includes('dev-fix'))
    ) {
      return true;
    }

    const id = current.id;
    if (id && (id.includes('devfix') || id.includes('dev-fix'))) {
      return true;
    }

    current = current.parentElement;
  }

  return false;
}
